import { Link, useNavigate } from "react-router-dom";
import { Badge, Button } from "@odingard/design-system";
import { ErrorState, LoadingState, EmptyState } from "@odingard/design-system";
import type { CatalogApplication, CatalogCustomerState } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api } from "../../lib/api";

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  return Math.max(0, Math.ceil(ms / (1000 * 60 * 60 * 24)));
}

// Apps the organization actively holds (subscribed or trialing).
const ACTIVE_STATES = new Set(["subscribed", "trialing"]);

export function MyApplications() {
  const { activeOrg } = useAuth();
  const navigate = useNavigate();
  const orgId = activeOrg?.id ?? "";
  const state = useAsync(
    async () => {
      const [apps, states] = await Promise.all([
        api.catalogApplications({ delivery: "platform", pageSize: 100 }),
        api.catalogState(orgId),
      ]);
      return { apps: apps.applications.filter((a) => a.delivery === "platform"), states: states.states };
    },
    [orgId],
  );

  const stateByApp = new Map((state.data?.states ?? []).map((s) => [s.slug, s]));
  const owned = (state.data?.apps ?? []).filter((a) => {
    const cs = stateByApp.get(a.slug);
    return cs && ACTIVE_STATES.has(cs.state);
  });

  function renderCard(app: CatalogApplication, cs: CatalogCustomerState) {
    const onTrial = cs.state === "trialing";
    const daysLeft = onTrial ? daysUntil(cs.trialEndsAt) : null;
    return (
      <div className="launch-card" key={app.id}>
        <div className="ods-row" style={{ justifyContent: "space-between" }}>
          <div className="launch-card__icon" aria-hidden="true">
            {app.icon}
          </div>
          {onTrial ? (
            <Badge tone="info">{daysLeft != null ? `Trial · ${daysLeft}d left` : "Trial"}</Badge>
          ) : (
            <Badge tone="success">Active</Badge>
          )}
        </div>
        <div>
          <h3 style={{ margin: "0 0 6px" }}>{app.name}</h3>
          <p style={{ color: "var(--ods-color-muted)", fontSize: "var(--ods-text-sm)", margin: 0 }}>{app.tagline || app.description}</p>
        </div>
        <div className="ods-row">
          {cs.canLaunch && (
            <Button size="sm" onClick={() => navigate(cs.route ?? app.route ?? "/studio")}>
              Launch
            </Button>
          )}
          {onTrial && cs.canSubscribe && (
            <Link to="/pricing">
              <Button size="sm" variant="ghost">
                Upgrade
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>My applications</h1>
          <p>Applications your organization has subscribed to or is trialing.</p>
        </div>
        <Link to="/marketplace">
          <Button variant="secondary" size="sm">
            Browse Marketplace
          </Button>
        </Link>
      </div>

      {state.loading && <LoadingState label="Loading your applications…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}
      {state.data && (
        owned.length === 0 ? (
          <EmptyState
            title="No active applications yet"
            description="Start a free trial from the Marketplace to add applications."
            action={
              <Link to="/marketplace">
                <Button size="sm">Browse Marketplace</Button>
              </Link>
            }
          />
        ) : (
          <div className="launcher-grid">
            {owned.map((app) => renderCard(app, stateByApp.get(app.slug)!))}
          </div>
        )
      )}
    </>
  );
}
